import {
  animate,
  motion,
  MotionValue,
  useMotionTemplate,
  useMotionValue,
} from "framer-motion";
import { useAtom } from "jotai";
import React, { useCallback, useEffect, useState } from "react";
import { CursorNameTag } from "./cursor/Cursor";
import { BOT_NAME, DEFAULT_CURSOR_SIZE } from "../consts";
import {
  audioStreamsAtom,
  currentUserAtom,
  presenceAtom,
} from "../jotai/atoms";
import { useUserInfos } from "../liveblocks/maps";
import { styled } from "../stitches.config";
import { Point2D, Position, Presence, User, Viewport } from "../types";
import { extractUserId } from "../utils/session";

function CursorIndicators() {
  const [currentUser] = useAtom(currentUserAtom);
  const [presences] = useAtom(presenceAtom);
  const [audios] = useAtom(audioStreamsAtom);
  const [userInfos] = useUserInfos();

  const currentUserScrollX = useMotionValue(0);
  const currentUserScrollY = useMotionValue(0);
  const currentUserScroll = useMotionTemplate`{"x":${currentUserScrollX}, "y":${currentUserScrollY}}`;

  // Control scroll with framer-motion because window.scrollTo() smooth doesn't work in Safari
  useEffect(() => {
    const unsub = currentUserScroll.onChange((scrollString) => {
      const scroll: Point2D = JSON.parse(scrollString);
      window.scrollTo(scroll.x, scroll.y);
    });
    return () => {
      unsub();
    };
  }, [currentUserScroll]);

  if (!userInfos) return null;

  const audioUids = new Set<User["id"]>();
  for (const sid in audios) {
    const uid = extractUserId(sid);
    if (uid) {
      audioUids.add(uid);
    }
  }

  const others = Object.entries(presences).filter(
    ([uid]) => uid !== currentUser?.id
  );

  return (
    <IndicatorsContainer>
      {others
        .filter(
          ([uid, presence]) =>
            userInfos[uid] &&
            userInfos[uid].name !== BOT_NAME &&
            presence.position &&
            presence.viewport
        )
        .map(([uid, presence]) => (
          <CursorIndicator
            key={uid}
            isMuted={!audioUids.has(uid)}
            otherUserInfo={userInfos[uid]}
            otherUserPresence={presence}
            currentUserScrollXMotionValue={currentUserScrollX}
            currentUserScrollYMotionValue={currentUserScrollY}
          />
        ))}
    </IndicatorsContainer>
  );
}

export default React.memo(CursorIndicators);

function CursorIndicator({
  otherUserInfo,
  otherUserPresence,
  isMuted,
  currentUserScrollXMotionValue,
  currentUserScrollYMotionValue,
}: {
  otherUserInfo: User;
  otherUserPresence: Pick<Presence, "position" | "viewport">;
  isMuted: boolean;
  currentUserScrollXMotionValue: MotionValue;
  currentUserScrollYMotionValue: MotionValue;
}) {
  const [show, setShow] = useState(false);

  /**
   * These are like the model of "position: absolute".
   * By default, offsetX/Y are from left/top.
   */
  const offsetX = useMotionValue(0);
  const offsetY = useMotionValue(0);
  const [isOffsetXFromRight, setIsOffsetXFromRight] = useState(false);
  const [isOffsetYFromBottom, setIsOffsetYFromBottom] = useState(false);

  const [currentUserScrollX, setCurrentUserScrollX] = useState(
    () => window.scrollX
  );
  const [currentUserScrollY, setCurrentUserScrollY] = useState(
    () => window.scrollY
  );

  /** Track current user scroll position. */
  useEffect(() => {
    function onScroll() {
      setCurrentUserScrollX(window.scrollX);
      setCurrentUserScrollY(window.scrollY);
    }

    window.addEventListener("scroll", onScroll);

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /**
   * Update indicator position when other user moves cursor or
   * current user scroll window.
   */
  useEffect(() => {
    const winW = window.innerWidth;
    const winH = window.innerHeight;

    const show = !isPositionInViewport(otherUserPresence.position, {
      position: { x: currentUserScrollX, y: currentUserScrollY },
      size: { width: winW, height: winH },
    });

    setShow(show);

    if (show) {
      /**
       * Map other user's position to indicator position, whose valid range
       * is the edge of the viewport.
       *
       * Intuitively, the mapping would be
       *
       * const indicatorX = clamp(
       *   otherUserPresence.position.x,
       *   scroll.current.x,
       *   scroll.current.x + window.innerWidth
       * );
       *
       * but the result of that is in the coordinates of the space, so
       * we need to transform it to the coordinates of the viewport
       * (between 0 and window width/height).
       */
      const newIndicatorX = clamp(
        otherUserPresence.position.x - currentUserScrollX,
        0,
        winW
      );
      const newIndicatorY = clamp(
        otherUserPresence.position.y - currentUserScrollY,
        0,
        winH
      );

      offsetX.set(
        newIndicatorX > winW / 2 ? newIndicatorX - winW : newIndicatorX
      );
      offsetY.set(
        newIndicatorY > winH / 2 ? newIndicatorY - winH : newIndicatorY
      );
      setIsOffsetXFromRight(newIndicatorX > winW / 2);
      setIsOffsetYFromBottom(newIndicatorY > winH / 2);
    }
  }, [
    otherUserPresence,
    offsetX,
    offsetY,
    currentUserScrollX,
    currentUserScrollY,
  ]);

  const jumpToUser = useCallback(() => {
    const sourceX = currentUserScrollX;
    const sourceY = currentUserScrollY;
    const targetX = otherUserPresence.position.x - window.innerWidth / 2;
    const targetY = otherUserPresence.position.y - window.innerHeight / 2;
    currentUserScrollXMotionValue.set(otherUserPresence.viewport.position.x);
    currentUserScrollYMotionValue.set(otherUserPresence.viewport.position.y);
    const duration = convertRange(
      Math.sqrt(
        Math.pow(targetX - sourceX, 2) + Math.pow(targetY - sourceY, 2)
      ),
      [1600, 8000], // roughly diameter of furthest/shortest distances to scroll
      [0.3, 3]
    );
    animate(currentUserScrollXMotionValue, targetX, { duration });
    animate(currentUserScrollYMotionValue, targetY, { duration });
  }, [
    currentUserScrollXMotionValue,
    currentUserScrollYMotionValue,
    currentUserScrollX,
    currentUserScrollY,
    otherUserPresence.viewport.position.x,
    otherUserPresence.viewport.position.y,
    otherUserPresence.position.x,
    otherUserPresence.position.y,
  ]);

  if (!show) {
    return null;
  }

  return (
    <Cursor
      style={{
        x: offsetX,
        y: offsetY,
        right: isOffsetXFromRight ? 0 : undefined,
        bottom: isOffsetYFromBottom ? 0 : undefined,
      }}
      animate={{ opacity: show ? 1 : 0 }}
      transition={{ duration: 0.15 }}
      onClick={jumpToUser}
    >
      <CursorNameTag
        name={otherUserInfo.name}
        color={otherUserInfo.color}
        isMuted={isMuted}
      />
    </Cursor>
  );
}

const IndicatorsContainer = styled("ul", {
  position: "fixed",
  pointerEvents: "none",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
});

const Cursor = styled(motion.li, {
  pointerEvents: "all",
  position: "absolute",
  cursor: "pointer",
});

function clamp(v: number, min: number, max: number) {
  return Math.min(max, Math.max(v, min));
}
function convertRange(value: number, r1: number[], r2: number[]) {
  return ((value - r1[0]) * (r2[1] - r2[0])) / (r1[1] - r1[0]) + r2[0];
}

function isPositionInViewport(position: Position, viewport: Viewport) {
  // Give some space before we show cursor to prevent the two name tags being shown at once
  const delayPadding = 50;
  return (
    position.x > viewport.position.x - delayPadding - DEFAULT_CURSOR_SIZE &&
    position.y > viewport.position.y - delayPadding - DEFAULT_CURSOR_SIZE &&
    position.x < viewport.position.x + viewport.size.width + delayPadding &&
    position.y < viewport.position.y + viewport.size.height + delayPadding
  );
}
